import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";

export default function ResumePayment() {
  const { listingId } = useParams();
  const href = useHref();
  const t = useT();
  const catLabel = useCategoryLabel();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({ title: t("resume.seo.title") });

  useEffect(() => {
    api
      .get(`/listings/${listingId}/payment-summary`)
      .then(({ data }) => setSummary(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, [listingId]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/listings/${listingId}/resume-payment`);
      if (data.payment_url) {
        window.location.assign(data.payment_url);
        return;
      }
      toast.success(t("resume.toast.live"));
      setSummary((s) => ({ ...s, status: "approved" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center sm:px-8">
        <h1 data-testid="resume-error" className="font-display text-2xl font-bold tracking-tight">
          {t("resume.invalid")}
        </h1>
        <p className="mt-3 text-sm text-slate-600">{error}</p>
        <Link to={href("/submit")} className="mt-8 inline-block">
          <Button data-testid="resume-new-submission-button">{t("resume.newSubmit")}</Button>
        </Link>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="grid h-80 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const live = summary.status === "approved";

  return (
    <div className="mx-auto max-w-xl px-4 py-16 sm:px-8">
      <div data-testid="resume-payment-card" className="rounded-lg border border-border bg-card p-8 shadow-sm">
        <span className={`grid h-11 w-11 place-items-center rounded-md ${live ? "bg-emerald-600" : "bg-primary"} text-white`}>
          {live ? <CheckCircle2 className="h-5 w-5" /> : <Clock className="h-5 w-5" />}
        </span>
        <h1 className="mt-6 font-display text-2xl font-bold tracking-tight sm:text-3xl">
          {live ? t("resume.live.title") : t("resume.pending.title")}
        </h1>
        <p className="mt-3 text-base leading-relaxed text-slate-600">
          {live ? t("resume.live.body") : t("resume.pending.body")}
        </p>

        <dl className="mt-8 divide-y divide-border border-y border-border">
          {[
            [t("resume.row.business"), summary.business_name],
            [t("resume.row.category"), catLabel(summary.category)],
            [t("resume.row.location"), `${summary.district}, ${summary.state}`],
            [t("resume.row.amount"), summary.price_label],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between py-3">
              <dt className="text-sm text-slate-500">{k}</dt>
              <dd className="text-sm font-semibold text-slate-900">{v}</dd>
            </div>
          ))}
          <div className="flex items-center justify-between py-3">
            <dt className="text-sm text-slate-500">{t("resume.row.status")}</dt>
            <dd>
              <Badge
                data-testid="resume-status-badge"
                className={live ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-900"}
              >
                {summary.status}
              </Badge>
            </dd>
          </div>
        </dl>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          {live ? (
            <Link to={href(`/business/${listingId}`)}>
              <Button data-testid="resume-view-listing-button" className="bg-primary hover:bg-[#0052CC]">
                {t("resume.viewListing")}
              </Button>
            </Link>
          ) : (
            <Button
              data-testid="resume-pay-button"
              disabled={busy}
              onClick={pay}
              className="h-11 bg-primary hover:bg-[#0052CC]"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("resume.pay", { price: summary.price_label })}
            </Button>
          )}
          <Link to={href("/directory")}>
            <Button variant="outline" data-testid="resume-directory-button" className="h-11">
              {t("resume.browse")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
